import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Progress,
  VStack,
  HStack,
  Text,
  Button,
  useColorModeValue,
  useToast,
  Flex,
  Icon,
  Badge,
  Spinner,
  Center,
  Heading,
  useColorMode,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../assets/styles/colors';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiArrowRight, FiSkipForward, FiCheck, FiStar, FiZap } from 'react-icons/fi';
import { FaMoon, FaSun } from 'react-icons/fa';
import i18n from '../i18n';

// Import onboarding components
import Welcome from '../components/onboarding/Welcome';
import LanguageGoals from '../components/onboarding/LanguageGoals';
import SkillAssessment from '../components/onboarding/SkillAssessment';
import VocabularyBuilder from '../components/onboarding/VocabularyBuilder';
import FeatureTour from '../components/onboarding/FeatureTour';

// Import types and services
import {
  OnboardingProgress,
  ONBOARDING_STEPS,
  getNextStep,
  canSkipStep
} from '../types/onboarding';
import { OnboardingService } from '../services/onboardingService';
import logger from '../utils/logger';

const Onboarding: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { t } = useTranslation();
  const { colorMode, toggleColorMode } = useColorMode();
  // Force update state to trigger re-renders on theme change
  const [forceUpdate, setForceUpdate] = useState(0);

  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [stepData, setStepData] = useState<Record<string, unknown>>({});
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    // Don't redirect during initial loading
    if (isLoading || authLoading) return;

    // Check for force and demo parameters
    const isForceMode = location.search.includes("force=true");
    const isDemoMode = location.search.includes("demo=true");

    // If not logged in and not in force or demo mode, redirect to login
    if (!user && !isForceMode && !isDemoMode) {
      console.log("No authenticated user, redirecting to login");
      navigate("/login?redirect=/onboarding");
      toast({
        title: t("common.pleaseLogin"),
        description: t("common.pleaseLoginToView"),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [user, navigate, location.search, toast, t, isLoading, authLoading]);

  // Gaming-inspired theme colors
  const bgGradient = useColorModeValue(
    'linear(to-br, gray.50, blue.50, purple.50)',
    'linear(to-br, gray.900, blue.900, purple.900)'
  );
  const headerBg = useColorModeValue('white', colors.primary[800]);
  const footerBg = useColorModeValue('white', colors.primary[800]);
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('blue.500', 'blue.400');
  const successColor = useColorModeValue('green.500', 'green.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const progressBg = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const outlineBorderColor = useColorModeValue('gray.300', 'gray.600');

  // Enhanced toggle function to ensure complete theme change
  const handleToggleTheme = () => {
    toggleColorMode();
    // Force document body to update theme class
    document.body.dataset.theme = colorMode === 'light' ? 'dark' : 'light';
    // Update state to force component re-render
    setForceUpdate(prev => prev + 1);
    // Small delay to ensure all components update
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  };

  const loadOnboardingProgress = useCallback(async () => {
    // Check for demo mode or force mode
    const isForceMode = location.search.includes("force=true");
    const isDemoMode = location.search.includes("demo=true");

    // Wait for auth to initialize
    if (authLoading) return;

    // If not logged in and not in special modes, don't proceed (the redirect effect will handle this)
    if (!user && !isDemoMode && !isForceMode) {
      setIsLoading(false);
      return;
    }

    // If demo mode is explicitly requested, initialize with demo data
    if (isDemoMode) {
      console.log("🎮 Initializing onboarding in demo mode");

      setProgress({
        id: "demo",
        user_id: "demo-user",
        current_step: 1,
        completed_steps: [],
        assessment_results: {},
        language_goals: {},
        selected_vocabulary: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      setCurrentStep(1);
      setCompletedSteps([]);
      setStepData({});
      setIsLoading(false);

      // Remove the toast notification for demo mode
      // The user doesn't want to see this message
      return;
    }

    try {
      setIsLoading(true);

      let userProgress;
      try {
        userProgress = await Promise.race([
          OnboardingService.getOnboardingProgress(user.id),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Timeout")), 10000)
          ),
        ]);
      } catch (error) {
        userProgress = null;
      }

      // If no progress exists, initialize it
      if (!userProgress) {
        try {
          userProgress = await Promise.race([
            OnboardingService.initializeOnboarding(user.id),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Timeout")), 10000)
            ),
          ]);
        } catch (error) {
          // Create a fallback progress object
          userProgress = {
            id: "fallback",
            user_id: user.id,
            current_step: 1,
            completed_steps: [],
            assessment_results: {},
            language_goals: {},
            selected_vocabulary: [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
        }
      }

      // Check if onboarding is already completed (bypass if force=true)
      if (userProgress.completed_at && !isForceMode) {
        setShouldRedirect(true);
        return;
      }

      // If force mode is active, reset to step 1 for testing
      if (isForceMode) {
        setCurrentStep(1);
        setCompletedSteps([]);
        setStepData({});

        toast({
          title: "Force Mode Active",
          description: "Onboarding forced to restart for testing",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      } else {
        setCurrentStep(userProgress.current_step);
        setCompletedSteps(userProgress.completed_steps || []);
        setStepData({
          languageGoals: userProgress.language_goals || {},
          assessmentResults: userProgress.assessment_results || {},
          selectedVocabulary: userProgress.selected_vocabulary || [],
        });
      }

      setProgress(userProgress);
    } catch (error) {
      logger.error("Failed to load onboarding progress:", error);

      // Instead of showing error and staying in loading state,
      // initialize with default values to let user start fresh
      console.warn(
        "Initializing with default onboarding state due to error:",
        error
      );

      setProgress({
        id: "fallback",
        user_id: user.id,
        current_step: 1,
        completed_steps: [],
        assessment_results: {},
        language_goals: {},
        selected_vocabulary: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      setCurrentStep(1);
      setCompletedSteps([]);
      setStepData({});

      toast({
        title: t("onboarding.loadError"),
        description: t("onboarding.loadErrorDescription"),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, toast, t, location, authLoading]);

  // Load onboarding progress on mount
  useEffect(() => {
    // Always load onboarding progress, even without user (for demo mode)
    loadOnboardingProgress();
  }, [loadOnboardingProgress]);

  // Initialize RTL direction based on current language
  useEffect(() => {
    const currentLang = i18n.language;

    // Set document direction based on language
    if (currentLang === 'he') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = 'he';
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = currentLang;
    }

    console.log('Onboarding language direction set for:', currentLang);
  }, []);

  // Effect to ensure theme is applied consistently
  useEffect(() => {
    // Update data-theme attribute on body to force CSS cascade updates
    document.body.dataset.theme = colorMode;
    // Add a class to ensure theme is properly applied
    document.body.classList.remove('light-theme', 'dark-theme');
    document.body.classList.add(`${colorMode}-theme`);
  }, [colorMode, forceUpdate]);

  // Handle redirect after all hooks are called
  useEffect(() => {
    if (shouldRedirect) {
      navigate('/dashboard');
    }
  }, [shouldRedirect, navigate]);

  const saveProgress = async (step: number, data: Record<string, unknown> = {}) => {
    // In demo mode, just update local state without saving to backend
    if (!user || progress?.user_id === 'demo-user') {
      console.log('🎮 Demo mode: updating local state only');

      const updatedCompletedSteps = [...new Set([...completedSteps, step])];
      const updatedStepData = { ...stepData, ...data };

      setCompletedSteps(updatedCompletedSteps);
      setStepData(updatedStepData);
      return;
    }

    if (!progress) return;

    try {
      setIsSaving(true);

      const updatedCompletedSteps = [...new Set([...completedSteps, step])];
      const updatedStepData = { ...stepData, ...data };

      const progressUpdate: Partial<OnboardingProgress> = {
        completed_steps: updatedCompletedSteps,
        language_goals: updatedStepData.languageGoals || {},
        assessment_results: updatedStepData.assessmentResults || {},
        selected_vocabulary: updatedStepData.selectedVocabulary || []
      };

      await OnboardingService.updateOnboardingProgress(user.id, step, progressUpdate);

      setCompletedSteps(updatedCompletedSteps);
      setStepData(updatedStepData);
    } catch (error) {
      logger.error('Failed to save onboarding progress:', error);
      toast({
        title: t('onboarding.saveError'),
        description: t('onboarding.saveErrorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async (data?: Record<string, unknown>) => {
    await saveProgress(currentStep, data);

    const nextStep = getNextStep(currentStep, completedSteps);
    if (nextStep > ONBOARDING_STEPS.length) {
      await completeOnboarding();
    } else {
      setCurrentStep(nextStep);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleReset = async () => {
    try {
      setIsLoading(true);
      // Clear local state
      setCurrentStep(1);
      setCompletedSteps([]);
      setStepData({});

      // Reset progress in database
      if (user) {
        await OnboardingService.initializeOnboarding(user.id);
      }

      toast({
        title: 'Onboarding Reset',
        description: 'Starting fresh from the beginning!',
        status: 'info',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      logger.error('Failed to reset onboarding:', error);
      toast({
        title: 'Reset Failed',
        description: 'Could not reset onboarding progress',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    if (canSkipStep(currentStep)) {
      await handleNext();
    }
  };

  const completeOnboarding = async () => {
    // In demo mode, just show completion message and redirect
    if (progress?.user_id === 'demo-user') {
      console.log('🎮 Demo mode: completing onboarding locally');

      toast({
        title: 'Demo Completed!',
        description: 'Onboarding demo completed successfully. Sign up to save your progress and start learning!',
        status: 'success',
        duration: 8000,
        isClosable: true
      });

      // In demo mode, redirect to signup instead of dashboard
      navigate('/login?message=signup-to-continue');
      return;
    }

    // Ensure user is logged in before attempting to complete onboarding
    if (!user) {
      toast({
        title: t('common.pleaseLogin'),
        description: t('common.pleaseLoginToView'),
        status: 'warning',
        duration: 5000,
        isClosable: true
      });
      navigate('/login?redirect=/onboarding');
      return;
    }

    try {
      await OnboardingService.completeOnboarding(user.id);
      toast({
        title: t('onboarding.completed'),
        description: t('onboarding.completedDescription'),
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      navigate('/dashboard');
    } catch (error) {
      logger.error('Failed to complete onboarding:', error);
      toast({
        title: t('onboarding.completeError'),
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const renderCurrentStep = () => {
    const stepProps = {
      onNext: handleNext,
      onPrevious: handlePrevious,
      stepData,
      isLoading: isSaving
    };

    switch (currentStep) {
      case 1:
        return <Welcome {...stepProps} />;
      case 2:
        return <LanguageGoals {...stepProps} />;
      case 3:
        return <VocabularyBuilder {...stepProps} />;
      case 4:
        return <FeatureTour {...stepProps} />;
      default:
        return <Welcome {...stepProps} />;
    }
  };

  const currentStepInfo = ONBOARDING_STEPS.find(step => step.id === currentStep);
  const progressPercentage = (completedSteps.length / ONBOARDING_STEPS.length) * 100;

  if (isLoading) {
    return (
      <Box minH="100vh" bgGradient={bgGradient}>
        <Center h="100vh">
          <VStack spacing={6}>
            <Box position="relative">
              <Spinner size="xl" color={accentColor} thickness="4px" />
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
              >
                <Icon as={FiZap} color={accentColor} fontSize="lg" />
              </Box>
            </Box>
            <VStack spacing={2}>
              <Heading size="md" color={textColor}>
                {t('onboarding.loading')}
              </Heading>
              <Text color={mutedTextColor} fontSize="sm">
                Preparing your learning journey...
              </Text>
            </VStack>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bgGradient={bgGradient} key={`theme-${colorMode}-${forceUpdate}`}>
      {/* Gaming-style Header - Only show for steps 1-3 */}
      {currentStep < 4 && (
        <Box
          bg={headerBg}
          borderBottom="1px"
          borderColor={borderColor}
          py={3}
          boxShadow="sm"
          className="css-1ozwyn0"
          data-testid="onboarding-header"
        >
          <Container maxW="container.xl">
            <VStack spacing={3}>
              {/* Progress Section */}
              <Box w="full">
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={2}>
                    <Box
                      w="6"
                      h="6"
                      borderRadius="lg"
                      bg={`linear-gradient(135deg, ${accentColor}, ${successColor})`}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color="white"
                      fontWeight="bold"
                      fontSize="xs"
                    >
                      {currentStep}
                    </Box>
                    <VStack spacing={0} align="start">
                      <Text fontSize="xs" color={mutedTextColor} fontWeight="medium">
                        {t('onboarding.step')} {currentStep} {t('onboarding.of')} {ONBOARDING_STEPS.length}
                      </Text>
                      <Text fontSize="xs" color={mutedTextColor}>
                        {Math.round(progressPercentage)}% {t('onboarding.complete')}
                      </Text>
                    </VStack>
                  </HStack>

                  <HStack spacing={2}>
                    {/* Theme Toggle Button */}
                    <Tooltip label={t(colorMode === 'light' ? 'common.darkModeTooltip' : 'common.lightModeTooltip')}>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        aria-label={t('common.toggleColorMode')}
                        icon={colorMode === 'light' ? <FaMoon /> : <FaSun />}
                        onClick={handleToggleTheme}
                        borderRadius="full"
                        _hover={{ bg: hoverBg }}
                      />
                    </Tooltip>

                    <Badge
                      colorScheme="blue"
                      variant="subtle"
                      borderRadius="full"
                      px={2}
                      py={1}
                      fontSize="xs"
                    >
                      <HStack spacing={1}>
                        <Icon as={FiStar} fontSize="xs" />
                        <Text fontSize="xs">
                          ~{currentStepInfo?.estimated_time} min
                        </Text>
                      </HStack>
                    </Badge>
                    {currentStepInfo?.can_skip && (
                      <Badge
                        colorScheme="gray"
                        variant="outline"
                        borderRadius="full"
                        px={2}
                        py={1}
                        fontSize="xs"
                      >
                        <Text fontSize="xs">{t('onboarding.optional')}</Text>
                      </Badge>
                    )}
                  </HStack>
                </HStack>

                <Progress
                  value={progressPercentage}
                  size="md"
                  borderRadius="full"
                  bg={progressBg}
                  sx={{
                    '& > div': {
                      background: `linear-gradient(90deg, ${accentColor}, ${successColor})`,
                    }
                  }}
                />
              </Box>

              {/* Current Step Info */}
              {currentStepInfo && (
                <Box w="full" textAlign="center">
                  <Heading
                    size="sm"
                    color={textColor}
                    bgGradient="linear(to-r, blue.400, purple.500)"
                    bgClip="text"
                    fontWeight="bold"
                  >
                    {currentStepInfo.title}
                  </Heading>
                </Box>
              )}
            </VStack>
          </Container>
        </Box>
      )}

      {/* Main Content - Full screen for steps 4+ */}
      <Box flex="1" className="css-1rr4qq7" data-testid="onboarding-content">
        {renderCurrentStep()}
      </Box>

      {/* Gaming-style Navigation Footer - Only show for steps 1-3 */}
      {currentStep < 4 && (
        <Box
          bg={footerBg}
          borderTop="1px"
          borderColor={borderColor}
          py={3}
          boxShadow="lg"
          className="css-4ndhwc"
          data-testid="onboarding-footer"
        >
          <Container maxW="container.xl">
            <Flex justify="space-between" align="center">
              <HStack spacing={2}>
                <Button
                  leftIcon={<Icon as={FiArrowLeft} />}
                  variant="ghost"
                  onClick={handlePrevious}
                  isDisabled={currentStep === 1}
                  color={textColor}
                  size="md"
                  borderRadius="xl"
                  _hover={{
                    bg: hoverBg,
                    transform: 'translateY(-1px)'
                  }}
                  _active={{
                    transform: 'translateY(0)'
                  }}
                  transition="all 0.2s ease"
                >
                  {t('onboarding.previous')}
                </Button>

                {currentStep > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleReset}
                    color={mutedTextColor}
                    fontSize="xs"
                    _hover={{
                      color: textColor,
                      bg: hoverBg
                    }}
                  >
                    Reset
                  </Button>
                )}
              </HStack>

              <HStack spacing={2}>
                {canSkipStep(currentStep) && (
                  <Button
                    leftIcon={<Icon as={FiSkipForward} />}
                    variant="outline"
                    onClick={handleSkip}
                    isLoading={isSaving}
                    color={textColor}
                    borderColor={outlineBorderColor}
                    size="md"
                    borderRadius="xl"
                    _hover={{
                      borderColor: accentColor,
                      color: accentColor,
                      transform: 'translateY(-1px)'
                    }}
                    _active={{
                      transform: 'translateY(0)'
                    }}
                    transition="all 0.2s ease"
                  >
                    {t('onboarding.skip')}
                  </Button>
                )}

                <Button
                  rightIcon={<Icon as={currentStep === ONBOARDING_STEPS.length ? FiCheck : FiArrowRight} />}
                  onClick={() => handleNext()}
                  isLoading={isSaving}
                  loadingText={t('onboarding.saving')}
                  size="md"
                  borderRadius="xl"
                  px={6}
                  bgGradient="linear(to-r, blue.500, purple.500)"
                  color="white"
                  _hover={{
                    bgGradient: "linear(to-r, blue.600, purple.600)",
                    transform: "translateY(-2px)",
                    shadow: "lg"
                  }}
                  _active={{
                    transform: "translateY(0)",
                  }}
                  transition="all 0.3s ease"
                >
                  {currentStep === ONBOARDING_STEPS.length
                    ? t('onboarding.finish')
                    : t('onboarding.next')
                  }
                </Button>
              </HStack>
            </Flex>
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default Onboarding;